import React, { useState, useEffect } from "react";
import NeumorphismField from "../neumorphismField/neumorphismField";
import NeumorphicDropDwn from "../neumorphicDropDwn/neumorphicDropDwn";
import NeumorphismFieldMoney from "../neumorphismField/neumorphismFieldMoney";
import { paymentTypeOptions } from "../../constants/dropdownOptions";
import { ImgTopBtn } from "../buttons/index";
import { getStorageKey } from "../../utils/localStorageStateUniqueKeyGenerator";
import { periodChargeThresholdType } from "../../constants/dropdownOptions";
import { campaignAmountTypeErrorMessages } from "../../constants/errorMessages";
import "./campaignAmount.scss";

const CampaignAmount = ({ onSaveAmount, currentContextType, setErrorMessage }) => {
  // State Management
  const [totalBudget, setTotalBudget] = useState(() => {
    const storedTotalBudget = localStorage.getItem(
      getStorageKey("totalBudget", currentContextType)
    );
    console.log("Loaded totalBudget from localStorage:", storedTotalBudget);
    return storedTotalBudget ? parseFloat(storedTotalBudget) : 0; // Parse to float if value exists, default to 0 otherwise
  });
  const [paymentType, setPaymentType] = useState("");
  const [paymentSubscription, setPaymentSubscription] = useState("");
  const [campaignAdUrl, setCampaignAdUrl] = useState(() => {
    return (
      localStorage.getItem(getStorageKey("campaignAdUrl", currentContextType)) || ""
    );
  });
  const [costPerImpression, setCostPerImpression] = useState(0.01);
  const [costPerClick, setCostPerClick] = useState(0.5);
  const [selectedPeriodName, setSelectedPeriodName] = useState("");
  const [isCampaignAdUrlFocused, setCampaignAdUrlFocus] = useState(false);

  const [errors, setErrors] = useState({
    totalBudget: "",
    paymentType: "",
    paymentSubscription: "",
    campaignAdUrl: "",
  });

  // Focus and Dropdown States
  const [isCampaignBudgetFocused, setCampaignBudgetFocus] = useState(false);
  const [isCampaignTypeFocused, setCampaignTypeFocus] = useState(false);
  const [isBudgetPeriodFocused, setBudgetPeriodFocus] = useState(false);
  const [isPaymentTypeOpen, setPaymentTypeOpen] = useState(false);

  //   Regex Explanation:
  // ^ → Start of the string
  // (https?:\/\/)? → Optional http:// or https://
  // https? → Matches http or https
  // :\/\/ → Matches ://
  // ? → Makes it optional (so www.example.com would still match)
  // ([a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+.*)?
  // [a-zA-Z0-9-]+ → Matches the domain name (letters, numbers, hyphens)
  // (\.[a-zA-Z0-9-]+)+ → Ensures at least one dot (.) followed by a valid top-level domain (TLD) (like .com, .net, .org)
  // .* → Allows anything after the domain (like /path/to/page)
  // ? → Makes the entire domain optional (this part is likely unintentional—it shouldn't be optional)
  // pleae note this is a lose way of validating and good enough for version 1 launch till we get more experience people on the task
  const isValidAdUrl = (url) => {
    const regex = /^(https?:\/\/)?([a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+.*)?$/;
    return regex.test(url);
  };

  //   Potential Issues
  // ❌ This regex will allow invalid inputs like:
  // "randomtext" ✅ (But should fail ❌)
  // "htp://example" ✅ (Misspelled http)
  // "example." ✅ (Should fail since it has an incomplete TLD)

  //   Better URL Validation Regex
  // If you want stricter validation, try this:

  // js
  // Copy
  // Edit
  // const isValidAdUrl = (url) => {
  //   const regex = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+(\/.*)?$/;
  //   return regex.test(url);
  // };
  // What This Fixes:
  // ✅ Requires at least a domain + valid TLD (e.g., .com, .net)
  // ✅ Allows http, https, and www. (but doesn't require them)
  // ✅ Allows paths (/something) but doesn't force them
  // --> Not now because i want experience with user experience so we are starting losely unless
  // someone joins the team with how redirects to a website are handled with complaince and user experience

  const [isPaymentSubscriptionOpen, setPaymentSubscriptionOpen] =
    useState(false);

  // Load Saved Data on Mount
  useEffect(() => {
    const storedBudget = parseFloat(
      localStorage.getItem(getStorageKey("totalBudget", currentContextType)) || 0
    );
    const storedPaymentType = localStorage.getItem(
      getStorageKey("paymentType", currentContextType)
    );
    const storedSubscription = localStorage.getItem(
      getStorageKey("paymentSubscription", currentContextType)
    );

    if (storedBudget) setTotalBudget(storedBudget);
    if (storedPaymentType) setPaymentType(storedPaymentType);
    if (storedSubscription) setPaymentSubscription(storedSubscription);
  }, [currentContextType]);

  // Handlers for Inputs and Dropdowns
  const handlePaymentTypeChange = (e) => {
    const selectedValue = e.target.value;
    setPaymentType(selectedValue);

    // Set default cost per type and reset the other
    if (selectedValue === "impressions") {
      setCostPerImpression(0.01);
      setCostPerClick(0);
    } else if (selectedValue === "clicks") {
      setCostPerClick(0.5);
      setCostPerImpression(0);
    }

    localStorage.setItem(
      getStorageKey("paymentType", currentContextType),
      selectedValue
    );
  };

  const handlePaymentSubscriptionChange = (e) => {
    const selectedValue = e.target.value;
    setPaymentSubscription(selectedValue);

    const selectedPeriod = periodChargeThresholdType.find(
      (option) => option.value === selectedValue
    );
    if (selectedPeriod) {
      setSelectedPeriodName(selectedPeriod.name);
    }

    localStorage.setItem(
      getStorageKey("paymentSubscription", currentContextType),
      selectedValue
    );
  };
  const handleCampaignAdUrlChange = (e) => {
    const value = e.target.value;
    setCampaignAdUrl(value);

    // Save to localStorage using your helper function
    localStorage.setItem(getStorageKey("campaignAdUrl", currentContextType), value);

    console.log("Saved campaignAdUrl:", value);
  };

  const handleCampaignBudgetFocus = () => {
    setCampaignBudgetFocus(true);
    setErrors((prev) => ({ ...prev, totalBudget: "" }));
  };

  const handleCampaignBudgetBlur = () => {
    setCampaignBudgetFocus(false);
  };

  const handleCampaignTypeFocus = () => {
    setCampaignTypeFocus(true);
    setErrors((prevErrors) => ({ ...prevErrors, paymentType: "" }));
  };

  const handleBudgetPeriodFocus = () => {
    setBudgetPeriodFocus(true);
    setErrors((prevErrors) => ({ ...prevErrors, paymentSubscription: "" }));
  };
  const handleCampaignAdUrlFocus = () => {
    setCampaignAdUrlFocus(true);
    setErrors((prev) => ({ ...prev, campaignAdUrl: "" })); // Clear error
  };

  const handleCampaignAdUrlBlur = () => {
    setCampaignAdUrlFocus(false);
  };

  const saveAmount = () => {
    const newErrors = {
      totalBudget: "",
      paymentType: "",
      paymentSubscription: "",
    };

    let isValid = true;

    // Validate total budget
    if (!totalBudget || totalBudget <= 0) {
      newErrors.totalBudget = campaignAmountTypeErrorMessages.campaignBudget;
      console.log("Validation Error: Total Budget is invalid:", totalBudget);
      isValid = false;
    } else {
      console.log("Total Budget is valid:", totalBudget);
    }

    // Validate payment type
    if (!paymentType) {
      newErrors.paymentType = campaignAmountTypeErrorMessages.paymentType;
      console.log("Validation Error: Payment Type is missing.");
      isValid = false;
    } else {
      console.log("Payment Type is valid:", paymentType);
    }
    // a valid campaign ad must be collected for clicks
    if (
      paymentType === "clicks" &&
      (!campaignAdUrl || !isValidAdUrl(campaignAdUrl))
    ) {
      newErrors.campaignAdUrl = campaignAmountTypeErrorMessages.campiagnAdUrl; // Use error message from constants
      console.log("Validation Error: Campaign Ad URL is invalid or missing.");
      isValid = false;
    } else if (paymentType === "clicks") {
      console.log("Campaign Ad URL is valid:", campaignAdUrl);
    }

    // Validate payment subscription
    if (!paymentSubscription) {
      newErrors.paymentSubscription =
        campaignAmountTypeErrorMessages.paymentSubscription;
      console.log("Validation Error: Payment Subscription is missing.");
      isValid = false;
    } else {
      console.log("Payment Subscription is valid:", paymentSubscription);
    }

    // Update the error state after initial validation
    setErrors(newErrors);
    if (!isValid) {
      console.log("Validation failed at the first stage. Exiting.");
      return; // Exit if the initial validation fails
    }

    // Fetch necessary data from localStorage
    const dailyCostLimit = parseFloat(
      localStorage.getItem(getStorageKey("dailyCostLimit", currentContextType)) || "0"
    );
    const campaignExpirationDate = new Date(
      localStorage.getItem(getStorageKey("campaignExpirationDate", currentContextType))
    );
    const costPerImpression = parseFloat(
      localStorage.getItem(getStorageKey("costPerImpression", currentContextType)) ||
        "0"
    );
    const costPerClick = parseFloat(
      localStorage.getItem(getStorageKey("costPerClick", currentContextType)) || "0"
    );

    const currentDate = new Date();
    const timeDifference =
      campaignExpirationDate.getTime() - currentDate.getTime();
    const campaignDuration = Math.max(
      Math.ceil(timeDifference / (1000 * 3600 * 24)),
      1
    );

    console.log("Fetched Data:");
    console.log("Daily Cost Limit:", dailyCostLimit);
    console.log("Campaign Expiration Date:", campaignExpirationDate);
    console.log("Current Date:", currentDate);
    console.log("Campaign Duration (days):", campaignDuration);

    const minimalDailyCost =
      paymentType === "impressions" ? costPerImpression : costPerClick;
    const minimalTotalBudget = minimalDailyCost * campaignDuration;

    console.log("Minimal Daily Cost:", minimalDailyCost);
    console.log("Minimal Total Budget:", minimalTotalBudget);

    // Budget validation logic
    if (dailyCostLimit === 0) {
      const minimalCost = campaignDuration * minimalDailyCost;

      console.log(
        `Validation Error: Daily Cost Limit is 0. Budget (${totalBudget}) cannot sustain the campaign for ${campaignDuration} days.`
      );
      setErrorMessage(
        `Your daily cost limit is too low for the selected campaign duration. To run your campaign for ${campaignDuration} days, you need at least $${minimalCost.toFixed(
          2
        )} for minimal coverage (1 ${paymentType} per day).`
      );
      isValid = false;
    } else if (totalBudget < minimalTotalBudget) {
      const shortfall = minimalTotalBudget - totalBudget;

      console.log(
        `Validation Error: Total Budget (${totalBudget}) is less than Required Budget (${minimalTotalBudget}).`
      );
      setErrorMessage(
        `Your total budget is insufficient. You need $${shortfall.toFixed(
          2
        )} more to run your campaign for ${campaignDuration} days with minimal coverage (1 ${paymentType} per day).`
      );
      isValid = false;
    } else {
      console.log("Budget validation passed.");
      // Save `totalBudget` to local storage
      localStorage.setItem(
        getStorageKey("totalBudget", currentContextType),
        totalBudget.toString()
      );
      console.log("Total Budget saved to localStorage:", totalBudget);

      // Concatenate `totalBudget` and pass it to the next process
      console.log("All validations passed. Proceeding to save the amount.");
      onSaveAmount({
        totalBudget, // Only passing totalBudget for now
      });
    }

    if (!isValid) {
      console.log("Validation failed at the second stage. Exiting.");
      return; // Exit if the new validation fails
    }

    // Proceed to save the amount
    console.log("All validations passed. Proceeding to save the amount.");
    onSaveAmount({
      totalBudget,
      paymentType,
      paymentSubscription,
      costPerImpression,
      costPerClick,
      ...(paymentType === "clicks" && { campaignAdUrl }), // Include campaignAdUrl only if paymentType is "clicks"
    });
  };

  return (
    <div id="SetCampaignTypeAmount" className="createCampaignPopup">
      <div className="createCampaignContent">
        <NeumorphismFieldMoney
          label="Total Budget:"
          value={totalBudget}
          placeholder="Enter Amount" // Default placeholder text
          onChange={(newValue) => setTotalBudget(newValue)}
          currentContextType={currentContextType}
          totalBudget={totalBudget}
          setTotalBudget={setTotalBudget}
          setPaymentType={setPaymentType}
          costPerImpression={costPerImpression}
          paymentSubscription={paymentSubscription}
          costPerClick={costPerClick}
          paymentType={paymentType}
          error={errors.totalBudget} // Pass the error message
          className={`campaign-budget-input ${errors.totalBudget && !isCampaignBudgetFocused ? "error-message error-border" : ""}`}
          onFocus={handleCampaignBudgetFocus}
          onBlur={handleCampaignBudgetBlur}
        />
        {(() => {
          switch (
            paymentType.toLowerCase() // Ensure it's case-insensitive
          ) {
            case "impressions":
              return (
                <p className="impressionContent">
                  Cost Per Impression: ${costPerImpression.toFixed(2)}
                </p>
              );

            case "clicks":
              return (
                <p className="clickContent">
                  Cost Per Click: ${costPerClick.toFixed(2)}
                </p>
              );

            default:
              return <p className="emptySpace"></p>;
          }
        })()}
        <div className="dropdown-wrapper">
          <NeumorphicDropDwn
            options={paymentTypeOptions}
            selectedOption={paymentType}
            setPaymentType={setPaymentType}
            setCostPerImpression={setCostPerImpression}
            setCostPerClick={setCostPerClick}
            onChange={handlePaymentTypeChange}
            placeholder={errors.paymentType || "Select Campaign Type"} // Use error message as placeholder if present
            isOpen={isPaymentTypeOpen}
            setOpen={setPaymentTypeOpen}
            onFocus={handleCampaignTypeFocus}
            className={`campaign-type-dropdown ${errors.paymentType && !isCampaignTypeFocused ? "error-message-style error-border" : ""}`} // Apply styles dynamically
            error={errors.paymentType} // Pass the error for dynamic value handling
          />
        </div>
        {paymentType === "clicks" && (
          <NeumorphismField
            label="Campaign Ad URL:"
            type="url"
            value={campaignAdUrl}
            placeholder="Enter URL (e.g., https://yourwebsite.com)"
            onChange={handleCampaignAdUrlChange} // Updated handler
            error={errors.campaignAdUrl} // Pass error message
            className={`campaign-ad-url-input ${
              errors.campaignAdUrl && !isCampaignAdUrlFocused
                ? "error-message error-border"
                : ""
            }`}
            onFocus={handleCampaignAdUrlFocus} // On focus // Clear error
            onBlur={handleCampaignAdUrlBlur} // Clear focus state
          />
        )}
        <div className="dropdown-wrapper">
          <NeumorphicDropDwn
            options={periodChargeThresholdType.map((option) => ({
              label: option.name,
              value: option.value,
            }))}
            selectedOption={paymentSubscription}
            onChange={handlePaymentSubscriptionChange}
            placeholder="Select Budget Period"
            isOpen={isPaymentSubscriptionOpen}
            setOpen={setPaymentSubscriptionOpen}
            onFocus={handleBudgetPeriodFocus}
            error={errors.paymentSubscription} // Pass the error message for dynamic placeholder
            className={`campaign-budgetPeriod-dropdown ${errors.paymentSubscription ? "error-border" : ""}`} // Dynamically apply error-border class
          />
        </div>
        <div className="amount-button">
          <ImgTopBtn
            imgTopBtnText="Save Amount and Checkout"
            onClick={saveAmount}
            imgTopBtnWidth="200px"
            imgTopBtnFontColor="var(--color-bloodred)"
            position="relative"
            extraImgTopBtnStyles={{
              backgroundImage:
                "linear-gradient(120deg, #e0e0e0 0%, #fefefe 80%)",
            }}
          />
        </div>
      </div>
    </div>
  );
};
export default CampaignAmount;
